import React, { useMemo } from "react";
import type { BannerSwapOverlapAnalysis } from "../../../utils/@types";
import type { SwapOverlapResolutionChoice } from "./SwapOverlapResolutionDialog";
import "./OverlapResolutionDiagram.css";

type SegmentKind = "existing" | "incoming" | "overlap" | "cut";

type LaneSegment = {
  id: string;
  kind: SegmentKind;
  left: number;
  width: number;
  label?: string;
  priority?: boolean;
};

type LaneDiagram = {
  lanes: Array<{
    id: string;
    label: string;
    segments: LaneSegment[];
  }>;
  showOverlapLegend: boolean;
  caption?: string;
};

const MIN_SEGMENT_WIDTH = 8;

function toMs(value: string | Date | undefined): number | null {
  if (!value) {
    return null;
  }
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function shortChannelId(channelId: string): string {
  if (channelId.length <= 14) {
    return channelId;
  }
  return `${channelId.slice(0, 8)}…${channelId.slice(-4)}`;
}

function pct(value: number, start: number, span: number): number {
  return ((value - start) / span) * 100;
}

function clampWidth(width: number): number {
  return Math.max(width, MIN_SEGMENT_WIDTH);
}

function clampLeft(left: number, width: number): number {
  return Math.max(0, Math.min(left, 100 - width));
}

/** Map a time range onto lane segments with a minimum visible width. */
function segmentFromRange(
  id: string,
  kind: SegmentKind,
  startMs: number,
  endMs: number,
  rangeStart: number,
  span: number,
  opts?: { label?: string; priority?: boolean; minWidth?: number }
): LaneSegment | null {
  if (span <= 0) {
    return null;
  }

  let left = pct(startMs, rangeStart, span);
  let width =
    endMs > startMs
      ? pct(endMs, rangeStart, span) - left
      : opts?.minWidth ?? MIN_SEGMENT_WIDTH;

  width = Math.max(width, opts?.minWidth ?? MIN_SEGMENT_WIDTH);
  left = clampLeft(left, width);

  return {
    id,
    kind,
    left,
    width,
    label: opts?.label,
    priority: opts?.priority,
  };
}

function getConflictContext(overlap: BannerSwapOverlapAnalysis) {
  const conflict = overlap.conflicts[0];
  const existing = conflict?.conflictingSwaps[0];
  const incoming = overlap.preview?.remove_conflicts.channels.find((ch) =>
    overlap.conflictChannelIds.includes(ch.channelId)
  );
  if (!conflict || !existing || !incoming) {
    return null;
  }

  const existingStart = toMs(existing.startDate);
  const existingEnd = toMs(existing.endDate);
  const incomingStart = toMs(incoming.startDate);
  const incomingEnd = toMs(incoming.endDate);
  if (
    existingStart == null ||
    existingEnd == null ||
    incomingStart == null ||
    incomingEnd == null
  ) {
    return null;
  }

  return {
    conflict,
    existing,
    existingStart,
    existingEnd,
    incomingStart,
    incomingEnd,
  };
}

function buildTailExistingLanes(
  overlap: BannerSwapOverlapAnalysis
): LaneDiagram | null {
  const ctx = getConflictContext(overlap);
  const previewChannel = overlap.preview?.tail_existing_priority.channels.find(
    (ch) => overlap.conflictChannelIds.includes(ch.channelId)
  );
  if (!ctx || !previewChannel) {
    return null;
  }

  const tailedStart = toMs(previewChannel.startDate) ?? ctx.existingEnd;
  const tailedEnd = toMs(previewChannel.endDate) ?? tailedStart;
  const rangeStart = Math.min(ctx.existingStart, tailedStart);
  const rangeEnd = Math.max(ctx.existingEnd, tailedEnd, tailedStart + 1);
  const span = Math.max(rangeEnd - rangeStart, 1);

  const existingSeg = segmentFromRange(
    "existing",
    "existing",
    ctx.existingStart,
    ctx.existingEnd,
    rangeStart,
    span,
    {
      label: `#${ctx.existing.swapUID}`,
      priority: true,
    }
  );

  const incomingSeg = segmentFromRange(
    "incoming",
    "incoming",
    tailedStart,
    Math.max(tailedEnd, tailedStart + span * 0.08),
    rangeStart,
    span,
    {
      label: "Your swap",
      minWidth: MIN_SEGMENT_WIDTH,
    }
  );

  return {
    lanes: [
      {
        id: "existing",
        label: "Existing",
        segments: existingSeg ? [existingSeg] : [],
      },
      {
        id: "incoming",
        label: "Your swap",
        segments: incomingSeg ? [incomingSeg] : [],
      },
    ],
    showOverlapLegend: false,
    caption:
      "Existing finishes first; your swap keeps its end date and starts when there is room.",
  };
}

function buildTailIncomingLanes(
  overlap: BannerSwapOverlapAnalysis
): LaneDiagram | null {
  const ctx = getConflictContext(overlap);
  const adjusted = overlap.preview?.tail_incoming_priority.adjustedBlockers.find(
    (b) => b.channelId === ctx?.conflict.channelId
  );
  if (!ctx) {
    return null;
  }

  const adjustedEnd =
    toMs(adjusted?.newEndDate) ?? ctx.incomingStart ?? ctx.existingEnd;
  const rangeStart = Math.min(ctx.existingStart, ctx.incomingStart);
  const rangeEnd = Math.max(ctx.existingEnd, ctx.incomingEnd, adjustedEnd);
  const span = Math.max(rangeEnd - rangeStart, 1);

  const existingActive = segmentFromRange(
    "existing-active",
    "existing",
    ctx.existingStart,
    adjustedEnd,
    rangeStart,
    span,
    { label: `#${ctx.existing.swapUID}` }
  );

  const existingCut =
    adjustedEnd < ctx.existingEnd
      ? segmentFromRange(
          "existing-cut",
          "cut",
          adjustedEnd,
          ctx.existingEnd,
          rangeStart,
          span,
          { minWidth: MIN_SEGMENT_WIDTH * 0.75 }
        )
      : null;

  const incomingSeg = segmentFromRange(
    "incoming",
    "incoming",
    ctx.incomingStart,
    Math.max(ctx.incomingEnd, ctx.incomingStart + span * 0.08),
    rangeStart,
    span,
    {
      label: "Your swap",
      priority: true,
      minWidth: MIN_SEGMENT_WIDTH,
    }
  );

  return {
    lanes: [
      {
        id: "existing",
        label: "Existing",
        segments: [existingActive, existingCut].filter(Boolean) as LaneSegment[],
      },
      {
        id: "incoming",
        label: "Your swap",
        segments: incomingSeg ? [incomingSeg] : [],
      },
    ],
    showOverlapLegend: true,
    caption: "Existing ends early; your swap keeps its scheduled window.",
  };
}

function buildTruncateIncomingLanes(
  overlap: BannerSwapOverlapAnalysis
): LaneDiagram | null {
  const ctx = getConflictContext(overlap);
  const previewChannel = overlap.preview?.truncate_incoming_priority.channels.find(
    (ch) => overlap.conflictChannelIds.includes(ch.channelId)
  );
  if (!ctx || !previewChannel) {
    return null;
  }

  const truncatedEnd = toMs(previewChannel.endDate) ?? ctx.existingStart;
  const rangeStart = Math.min(ctx.incomingStart, ctx.existingStart);
  const rangeEnd = Math.max(ctx.incomingEnd, ctx.existingEnd, truncatedEnd);
  const span = Math.max(rangeEnd - rangeStart, 1);

  const incomingActive = segmentFromRange(
    "incoming-active",
    "incoming",
    ctx.incomingStart,
    truncatedEnd,
    rangeStart,
    span,
    { label: "Your swap", priority: true }
  );

  const incomingCut =
    truncatedEnd < ctx.incomingEnd
      ? segmentFromRange(
          "incoming-cut",
          "cut",
          truncatedEnd,
          ctx.incomingEnd,
          rangeStart,
          span,
          { minWidth: MIN_SEGMENT_WIDTH * 0.75 }
        )
      : null;

  const existingSeg = segmentFromRange(
    "existing",
    "existing",
    ctx.existingStart,
    ctx.existingEnd,
    rangeStart,
    span,
    { label: `#${ctx.existing.swapUID}` }
  );

  return {
    lanes: [
      {
        id: "incoming",
        label: "Your swap",
        segments: [incomingActive, incomingCut].filter(Boolean) as LaneSegment[],
      },
      {
        id: "existing",
        label: "Existing",
        segments: existingSeg ? [existingSeg] : [],
      },
    ],
    showOverlapLegend: true,
    caption: "Your swap ends when the existing swap starts.",
  };
}

function buildDelayExistingStartLanes(
  overlap: BannerSwapOverlapAnalysis
): LaneDiagram | null {
  const ctx = getConflictContext(overlap);
  const adjusted = overlap.preview?.delay_existing_start_priority.adjustedPeers.find(
    (p) => p.channelId === ctx?.conflict.channelId
  );
  if (!ctx) {
    return null;
  }

  const delayedStart =
    toMs(adjusted?.newStartDate) ?? ctx.incomingEnd ?? ctx.existingStart;
  const rangeStart = Math.min(ctx.incomingStart, ctx.existingStart);
  const rangeEnd = Math.max(ctx.incomingEnd, ctx.existingEnd, delayedStart);
  const span = Math.max(rangeEnd - rangeStart, 1);

  const incomingSeg = segmentFromRange(
    "incoming",
    "incoming",
    ctx.incomingStart,
    Math.max(ctx.incomingEnd, ctx.incomingStart + span * 0.08),
    rangeStart,
    span,
    {
      label: "Your swap",
      priority: true,
      minWidth: MIN_SEGMENT_WIDTH,
    }
  );

  const existingCut =
    delayedStart > ctx.existingStart
      ? segmentFromRange(
          "existing-cut",
          "cut",
          ctx.existingStart,
          delayedStart,
          rangeStart,
          span,
          { minWidth: MIN_SEGMENT_WIDTH * 0.75 }
        )
      : null;

  const existingActive = segmentFromRange(
    "existing-active",
    "existing",
    delayedStart,
    ctx.existingEnd,
    rangeStart,
    span,
    { label: `#${ctx.existing.swapUID}` }
  );

  return {
    lanes: [
      {
        id: "incoming",
        label: "Your swap",
        segments: incomingSeg ? [incomingSeg] : [],
      },
      {
        id: "existing",
        label: "Existing",
        segments: [existingCut, existingActive].filter(Boolean) as LaneSegment[],
      },
    ],
    showOverlapLegend: true,
    caption: "Existing starts after yours finishes; keeps its original end date.",
  };
}

function buildDropPeerLanes(
  overlap: BannerSwapOverlapAnalysis
): LaneDiagram | null {
  const ctx = getConflictContext(overlap);
  if (!ctx) {
    return null;
  }

  const rangeStart = Math.min(ctx.incomingStart, ctx.existingStart);
  const rangeEnd = Math.max(ctx.incomingEnd, ctx.existingEnd);
  const span = Math.max(rangeEnd - rangeStart, 1);

  const incomingSeg = segmentFromRange(
    "incoming",
    "incoming",
    ctx.incomingStart,
    Math.max(ctx.incomingEnd, ctx.incomingStart + span * 0.08),
    rangeStart,
    span,
    {
      label: "Your swap",
      priority: true,
      minWidth: MIN_SEGMENT_WIDTH,
    }
  );

  const existingRemoved = segmentFromRange(
    "existing-removed",
    "cut",
    ctx.existingStart,
    ctx.existingEnd,
    rangeStart,
    span,
    {
      label: `#${ctx.existing.swapUID}`,
      minWidth: MIN_SEGMENT_WIDTH,
    }
  );

  return {
    lanes: [
      {
        id: "incoming",
        label: "Your swap",
        segments: incomingSeg ? [incomingSeg] : [],
      },
      {
        id: "existing",
        label: "Existing",
        segments: existingRemoved ? [existingRemoved] : [],
      },
    ],
    showOverlapLegend: true,
    caption: "Conflicting channels are removed from the existing swap; yours is unchanged.",
  };
}

function buildConflictPreviewLanes(
  overlap: BannerSwapOverlapAnalysis
): LaneDiagram | null {
  const ctx = getConflictContext(overlap);
  if (!ctx) {
    return null;
  }

  const overlapStart = Math.max(ctx.existingStart, ctx.incomingStart);
  const overlapEnd = Math.min(ctx.existingEnd, ctx.incomingEnd);
  const rangeStart = Math.min(ctx.existingStart, ctx.incomingStart);
  const rangeEnd = Math.max(ctx.existingEnd, ctx.incomingEnd);
  const span = Math.max(rangeEnd - rangeStart, 1);

  const existingSeg = segmentFromRange(
    "existing",
    "existing",
    ctx.existingStart,
    ctx.existingEnd,
    rangeStart,
    span,
    { label: `#${ctx.existing.swapUID}` }
  );

  const incomingSeg = segmentFromRange(
    "incoming",
    "incoming",
    ctx.incomingStart,
    Math.max(ctx.incomingEnd, ctx.incomingStart + span * 0.08),
    rangeStart,
    span,
    {
      label: "Your swap",
      minWidth: MIN_SEGMENT_WIDTH,
    }
  );

  const overlapSeg =
    overlapEnd > overlapStart
      ? segmentFromRange(
          "overlap",
          "overlap",
          overlapStart,
          overlapEnd,
          rangeStart,
          span,
          { minWidth: MIN_SEGMENT_WIDTH * 0.75 }
        )
      : null;

  return {
    lanes: [
      {
        id: "existing",
        label: "Existing",
        segments: [existingSeg, overlapSeg].filter(Boolean) as LaneSegment[],
      },
      {
        id: "incoming",
        label: "Your swap",
        segments: [incomingSeg, overlapSeg].filter(Boolean) as LaneSegment[],
      },
    ],
    showOverlapLegend: true,
  };
}

function LaneTrack({
  segments,
  compact,
}: {
  segments: LaneSegment[];
  compact?: boolean;
}) {
  return (
    <div className="overlap-diagram__track">
      {segments.map((seg) => (
        <div
          key={seg.id}
          className={`overlap-diagram__bar overlap-diagram__bar--${seg.kind}${
            seg.priority ? " overlap-diagram__bar--priority" : ""
          }`}
          style={{ left: `${seg.left}%`, width: `${seg.width}%` }}
          title={seg.label}
        >
          {!compact && seg.width >= 14 && seg.label ? (
            <span className="overlap-diagram__bar-label">{seg.label}</span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function LaneDiagramView({
  diagram,
  tone,
  compact,
}: {
  diagram: LaneDiagram;
  tone: string;
  compact?: boolean;
}) {
  return (
    <div
      className={`overlap-diagram overlap-diagram--${tone}${
        compact ? " overlap-diagram--compact" : ""
      }`}
    >
      <div className="overlap-diagram__lanes">
        {diagram.lanes.map((lane) => (
          <div key={lane.id} className="overlap-diagram__lane">
            <span className="overlap-diagram__lane-label">{lane.label}</span>
            <LaneTrack segments={lane.segments} compact={compact} />
          </div>
        ))}
      </div>
      <div className="overlap-diagram__axis">
        <span>Earlier</span>
        <span>Later</span>
      </div>
      <div className="overlap-diagram__legend">
        <span className="overlap-diagram__legend-item">
          <i className="overlap-diagram__swatch overlap-diagram__swatch--existing" />
          Existing swap
        </span>
        <span className="overlap-diagram__legend-item">
          <i className="overlap-diagram__swatch overlap-diagram__swatch--incoming" />
          Your swap
        </span>
        {diagram.showOverlapLegend ? (
          <span className="overlap-diagram__legend-item">
            <i className="overlap-diagram__swatch overlap-diagram__swatch--overlap" />
            Overlap / cut
          </span>
        ) : null}
      </div>
      {!compact && diagram.caption ? (
        <p className="overlap-diagram__caption">{diagram.caption}</p>
      ) : null}
    </div>
  );
}

type Props = {
  option: SwapOverlapResolutionChoice;
  overlap: BannerSwapOverlapAnalysis;
  compact?: boolean;
};

const OPTION_CHIP: Record<
  SwapOverlapResolutionChoice,
  { letter: string; tone: string }
> = {
  remove_conflicts: { letter: "A", tone: "remove" },
  tail_existing_priority: { letter: "B", tone: "tail" },
  tail_incoming_priority: { letter: "C", tone: "priority" },
  truncate_incoming_priority: { letter: "B", tone: "truncate" },
  delay_existing_start_priority: { letter: "C", tone: "delay" },
  drop_peer_conflicts: { letter: "D", tone: "drop-peer" },
};

export const OverlapResolutionDiagram: React.FC<Props> = ({
  option,
  overlap,
  compact = false,
}) => {
  const laneDiagram = useMemo(() => {
    switch (option) {
      case "tail_existing_priority":
        return buildTailExistingLanes(overlap);
      case "tail_incoming_priority":
        return buildTailIncomingLanes(overlap);
      case "truncate_incoming_priority":
        return buildTruncateIncomingLanes(overlap);
      case "delay_existing_start_priority":
        return buildDelayExistingStartLanes(overlap);
      case "drop_peer_conflicts":
        return buildDropPeerLanes(overlap);
      default:
        return null;
    }
  }, [option, overlap]);

  const chip = OPTION_CHIP[option];

  if (option === "remove_conflicts") {
    const conflictIds = new Set(overlap.conflictChannelIds);
    const kept = overlap.cleanChannelIds;
    return (
      <div
        className={`overlap-diagram overlap-diagram--${chip.tone}${
          compact ? " overlap-diagram--compact" : ""
        }`}
      >
        <div className="overlap-diagram__channel-row">
          {kept.length > 0 ? (
            kept.slice(0, 3).map((id) => (
              <span
                key={id}
                className="overlap-diagram__channel-chip overlap-diagram__channel-chip--kept"
              >
                {shortChannelId(id)}
              </span>
            ))
          ) : (
            <span className="overlap-diagram__channel-chip overlap-diagram__channel-chip--empty">
              No channels left
            </span>
          )}
          {overlap.conflictChannelIds.slice(0, 3).map((id) => (
            <span
              key={id}
              className="overlap-diagram__channel-chip overlap-diagram__channel-chip--removed"
              title={id}
            >
              {shortChannelId(id)}
            </span>
          ))}
          {conflictIds.size > 3 ? (
            <span className="overlap-diagram__channel-chip overlap-diagram__channel-chip--more">
              +{conflictIds.size - 3}
            </span>
          ) : null}
        </div>
        <div className="overlap-diagram__legend">
          <span className="overlap-diagram__legend-item">
            <i className="overlap-diagram__swatch overlap-diagram__swatch--incoming" />
            Kept
          </span>
          <span className="overlap-diagram__legend-item">
            <i className="overlap-diagram__swatch overlap-diagram__swatch--removed" />
            Removed
          </span>
        </div>
      </div>
    );
  }

  if (!laneDiagram) {
    return null;
  }

  return (
    <LaneDiagramView diagram={laneDiagram} tone={chip.tone} compact={compact} />
  );
};

export const OverlapConflictPreview: React.FC<{
  overlap: BannerSwapOverlapAnalysis;
}> = ({ overlap }) => {
  const diagram = useMemo(
    () => buildConflictPreviewLanes(overlap),
    [overlap]
  );
  const conflict = overlap.conflicts[0];
  if (!diagram || !conflict) {
    return null;
  }

  return (
    <div className="overlap-resolution-dialog__conflict-preview">
      <p className="overlap-resolution-dialog__conflict-preview-title">
        Current conflict on {shortChannelId(conflict.channelId)}
      </p>
      <LaneDiagramView diagram={diagram} tone="conflict" compact />
    </div>
  );
};

export { OPTION_CHIP };
